import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { type FC, useLayoutEffect, useRef, useState } from "react";

import { Tooltip } from "@fronterahealth/frontera-ui-components";

import { RULE_CHIPS, type RuleChip } from "@pages/QARules/qaRuleFields";

// ---------------------------------------------------------------------------
// Structured rule editor pieces, shared by PackageCard / PackageRuleItem.
// ---------------------------------------------------------------------------

// Grid shared by the rules header row and each collapsed rule summary row so
// the columns line up: name | report type | location | kebab.
export const RULE_GRID = "grid grid-cols-[minmax(0,1fr)_84px_minmax(0,200px)_36px] items-center gap-3";
export const FIELD_LABEL = "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary";
export const EDITABLE_FIELD =
  "border border-border-medium bg-bg-default focus:border-globemallow-600 focus:outline-none focus:ring-2 focus:ring-globemallow-600/20";

// ---------------------------------------------------------------------------
// Kebab menu (hand-rolled, ported from the portal's local KebabMenu — Mosaic
// doesn't export an icon-only popover-menu primitive from its package root).
// ---------------------------------------------------------------------------

export interface MenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
}

export const KebabMenu: FC<{ ariaLabel: string; items: MenuItem[] }> = ({ ariaLabel, items }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative justify-self-end">
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        className="grid h-7 w-7 place-items-center rounded text-text-tertiary hover:bg-bg-disabled hover:text-text-primary"
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>
      {open && (
        <>
          {/* Full-screen backdrop closes the menu on any outside click. */}
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 top-8 z-30 min-w-[190px] rounded-lg border border-border-subtle bg-bg-surface p-1 shadow-lg">
            {items.map((it) => (
              <button
                key={it.label}
                type="button"
                onClick={() => {
                  setOpen(false);
                  it.onClick();
                }}
                className={clsx(
                  "block w-full rounded px-2.5 py-1.5 text-left text-sm hover:bg-bg-disabled",
                  it.danger ? "text-text-error-primary" : "text-text-primary",
                )}
              >
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Chip color depends on whether the type is selected and whether the chip is
// clickable — the collapsed row shows a read-only preview of the same chips.
const chipColor = (on: boolean, editable: boolean): string => {
  if (on) return "border-globemallow-600 bg-globemallow-600 text-white";
  if (editable) return "border-border-medium bg-bg-default text-text-secondary hover:border-globemallow-600";
  return "border-dashed border-border-subtle bg-transparent text-text-tertiary";
};

export const ReportTypeChips: FC<{
  types: RuleChip[];
  editable: boolean;
  onToggle: (t: RuleChip) => void;
}> = ({ types, editable, onToggle }) => (
  <div className="flex gap-1.5">
    {RULE_CHIPS.map((t) => {
      const on = types.includes(t);
      return (
        <button
          key={t}
          type="button"
          disabled={!editable}
          onClick={() => onToggle(t)}
          aria-pressed={on}
          className={clsx(
            "rounded border px-2.5 py-1 text-[11px] font-semibold tracking-wide transition-colors",
            editable ? "cursor-pointer" : "cursor-default",
            chipColor(on, editable),
          )}
        >
          {t}
        </button>
      );
    })}
  </div>
);

export const FieldLabelRow: FC<{ label: string; info?: string }> = ({ label, info }) => (
  <div className="mb-1.5 flex items-center gap-1">
    <span className={FIELD_LABEL}>{label}</span>
    {info && <Tooltip text={info} placement="top" showInfoIcon infoIconClassName="h-3.5 w-3.5 text-text-tertiary" />}
  </div>
);

export const InlineInput: FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
}> = ({ label, value, onChange, onBlur }) => (
  <div>
    <FieldLabelRow label={label} />
    <input
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      className={clsx("w-full rounded-md px-2.5 py-1.5 text-sm text-text-primary", EDITABLE_FIELD)}
    />
  </div>
);

export const AutoTextArea: FC<{
  label: string;
  value: string;
  info?: string;
  onChange: (v: string) => void;
  onBlur: () => void;
}> = ({ label, value, info, onChange, onBlur }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  // Grow to fit content (capped) so long term lists are readable without an
  // inner scrollbar — mirrors the portal's autosize behaviour.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    const need = el.scrollHeight + 2;
    const cap = 340;
    el.style.height = `${Math.min(need, cap)}px`;
    el.style.overflowY = need > cap ? "auto" : "hidden";
  }, [value]);
  return (
    <div className="mt-3.5">
      <FieldLabelRow label={label} info={info} />
      <textarea
        ref={ref}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={clsx(
          "block w-full resize-none rounded-md px-2.5 py-2 text-sm leading-relaxed text-text-primary",
          EDITABLE_FIELD,
        )}
      />
    </div>
  );
};
